<script setup>
import { ref, computed, getCurrentInstance } from 'vue';
import { useForm, router } from '@inertiajs/vue3';
import { useToast } from 'primevue/usetoast';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const { showLoading, hideLoading } = useLoading();

const props = defineProps({
    coins: Object,
    exchangeData: Object,
});

// State
const showPassword = ref(false);

// Initialize form
const form = useForm({
    name: '',
    email: '',
    password: '',
    phone: '',
    comission: '',
    comission_coin: 'BRL',
});

// Submit handler
function submit() {
    form.post(route('sellers.store'), {
        preserveScroll: true,
        onStart: () => showLoading('Creating seller...'),
        onFinish: () => hideLoading(),
        onSuccess: () => {
            toast.add({ 
                severity: 'success', 
                summary: $t('Success'), 
                detail: $t('Seller created successfully'), 
                life: 3000 
            });
        },
        onError: (errors) => {
            let detail = $t('Failed to create seller');
            if (errors.email) {
                const emailError = Array.isArray(errors.email) ? errors.email[0] : errors.email;
                if (emailError.toLowerCase().includes('taken') || emailError.toLowerCase().includes('unique')) {
                    detail = $t('This email is already registered.');
                } else {
                    detail = emailError;
                }
            } else {
                const errorMessages = Object.values(errors).flat();
                if (errorMessages.length > 0) {
                    detail = errorMessages.join(', ');
                }
            }
            toast.add({ 
                severity: 'error', 
                summary: $t('Error'), 
                detail: detail, 
                life: 3000 
            });
        }
    });
}

// Generate random password
function generatePassword() {
    form.password = Math.random().toString(36).slice(-8);
}

// Cancel handler
function cancel() {
    showLoading('Loading sellers...');
    router.visit(route('sellers.index'), { onFinish: () => hideLoading() });
}

const dynamicCurrencyOptions = computed(() => {
    if (!props.exchangeData) {
        return [
            { label: 'BRL', value: 'BRL' },
            { label: 'EUR', value: 'EUR' },
            { label: 'USD', value: 'USD' },
            { label: 'GBP', value: 'GBP' },
        ];
    }
    
    return props.exchangeData.map(exchange => ({
        label: exchange.currency,
        value: exchange.currency
    }));
});

defineOptions({
    layout: AdminMainLayout,
});

const translateFieldError = (error) => {
    if (!error) return error;
    const lower = error.toLowerCase();
    if (lower.includes('already been taken') || lower.includes('unique') || lower.includes('já está sendo utilizado')) {
        return $t('This email is already registered. Please use a different email.');
    }
    return error;
};
</script>

<template>
    <div>
        <!-- Header Card -->
        <Card class="mb-6">
            <template #content>
                <div class="flex items-center gap-3">
                    <Button 
                        icon="pi pi-arrow-left" 
                        severity="secondary" 
                        text 
                        rounded
                        @click="cancel"
                        v-tooltip.top="$t('Back to list')"
                    />
                    <div class="flex items-center gap-3">
                        <i class="pi pi-user-plus text-2xl text-primary-500"></i>
                        <div>
                            <h2 class="text-xl font-bold">{{ $t('Add seller') }}</h2>
                            <span class="text-sm text-surface-500">{{ $t('Create a new seller account') }}</span>
                        </div>
                    </div>
                </div>
            </template>
        </Card>

        <!-- Main Form Card -->
        <Card>
            <template #content>
                <form @submit.prevent="submit">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <!-- Name -->
                        <div class="field">
                            <label for="name" class="font-medium mb-2 block">
                                {{ $t('Username') }}
                                <span class="text-red-500">*</span>
                            </label>
                            <InputText 
                                id="name" 
                                v-model="form.name" 
                                class="w-full"
                                :class="{ 'p-invalid': form.errors.name }"
                                autocomplete="name"
                            />
                            <small v-if="form.errors.name" class="p-error">{{ form.errors.name }}</small>
                        </div>

                        <!-- Email -->
                        <div class="field">
                            <label for="email" class="font-medium mb-2 block">
                                {{ $t('Email') }}
                                <span class="text-red-500">*</span>
                            </label>
                            <InputText 
                                id="email" 
                                v-model="form.email" 
                                type="email"
                                class="w-full"
                                :class="{ 'p-invalid': form.errors.email }"
                                autocomplete="email"
                            />
                            <small v-if="form.errors.email" class="p-error">{{ translateFieldError(form.errors.email) }}</small>
                        </div>

                        <!-- Password -->
                        <div class="field">
                            <label for="password" class="font-medium mb-2 block">
                                {{ $t('Password') }}
                                <span class="text-red-500">*</span>
                            </label>
                            <div class="flex w-full gap-x-2">
                                <InputText
                                    id="password"
                                    v-model="form.password"
                                    :type="showPassword ? 'text' : 'password'"
                                    class="flex-1"
                                    :class="{ 'p-invalid': form.errors.password }"
                                    autocomplete="new-password"
                                />
                                <Button
                                    type="button"
                                    :icon="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"
                                    @click="showPassword = !showPassword"
                                    severity="secondary"
                                    v-tooltip.top="showPassword ? $t('Hide password') : $t('Show password')"
                                />
                                <Button
                                    type="button"
                                    icon="pi pi-refresh"
                                    @click="generatePassword"
                                    severity="info"
                                    v-tooltip.top="$t('Generate password')"
                                />
                            </div>
                            <small v-if="form.errors.password" class="p-error">{{ form.errors.password }}</small>
                        </div>

                        <!-- Phone -->
                        <div class="field">
                            <label for="phone" class="font-medium mb-2 block">
                                {{ $t('Phone') }}
                            </label>
                            <InputText 
                                id="phone" 
                                v-model="form.phone" 
                                type="tel"
                                class="w-full"
                                :class="{ 'p-invalid': form.errors.phone }"
                                autocomplete="tel"
                            />
                            <small v-if="form.errors.phone" class="p-error">{{ form.errors.phone }}</small>
                        </div>

                        <!-- Commission -->
                        <!-- <div class="field">
                            <label for="comission" class="font-medium mb-2 block">
                                {{ $t('Commission') }}
                            </label>
                            <div class="flex w-full gap-x-2">
                                <InputNumber
                                    id="comission"
                                    v-model="form.comission"
                                    mode="currency"
                                    :currency="form.comission_coin"
                                    locale="pt-BR"
                                    class="flex-1"
                                    :class="{ 'p-invalid': form.errors.comission }"
                                />
                                <Dropdown
                                    v-model="form.comission_coin"
                                    :options="dynamicCurrencyOptions"
                                    optionLabel="label"
                                    optionValue="value"
                                    class="pt-1 w-32"
                                />
                            </div>
                            <small v-if="form.errors.comission" class="p-error">{{ form.errors.comission }}</small>
                        </div> -->
                    </div>

                    <!-- Form Actions -->
                    <div class="flex justify-end gap-2 mt-6 pt-6 border-t">
                        <Button 
                            type="button"
                            :label="$t('Cancel')" 
                            severity="secondary" 
                            @click="cancel"
                        />
                        <Button 
                            type="submit"
                            :label="$t('Save')" 
                            icon="pi pi-check"
                            :loading="form.processing"
                            :disabled="form.processing"
                        />
                    </div>
                </form>
            </template>
        </Card>
    </div>
</template>